import { Controller } from "@hotwired/stimulus"

// ui--swipe-deck — the behaviour behind Ui::SwipeDeckComponent: a stack of
// slides, one visible at a time, moved by a sideways swipe on a touch screen or
// by the API (`next`, `previous`, `goTo`).
//
// Only the active slide is in the flow; the others are `hidden` + `inert`.
// During a swipe the neighbouring slide is shown beside the active one (still
// inert) and the track follows the finger with a transform written once per
// animation frame, so the gesture never triggers layout. One gesture moves at
// most one slide.
//
// A press becomes a swipe only once it has moved AXIS_LOCK_PX and mostly
// sideways; a mostly vertical move is left to the browser (the root carries
// `touch-action: pan-y pinch-zoom`, so vertical scrolling and pinch-zoom stay
// native and arrive here as `pointercancel`). Presses are ignored when they
// come from a mouse, a secondary pointer, a form control or anything marked
// `data-swipe-ignore`, or start within `edgeGuard` px of either screen edge,
// where the operating system's back gesture lives. A sideways drag inside
// something that scrolls sideways is left to it while it can still scroll
// that way. While the page is pinch-zoomed in the deck steps aside entirely:
// its touch-action is lifted, so the reader can pan sideways over it, and no
// press becomes a swipe.
//
// Events (dispatched on the root):
//   ui--swipe-deck:before-change  cancelable, detail {from, to, cause}
//   ui--swipe-deck:change         detail {from, to, cause}
//   ui--swipe-deck:overflow       swiped/stepped past either end, detail {direction, cause}
// `cause` is "swipe" or "api".

const AXIS_LOCK_PX = 10
const MIN_FLICK_PX = 30
const FLICK_VELOCITY_PX_PER_MS = 0.4
const RUBBER_BAND = 0.3
const DURATION_MS = 250
const CLICK_SUPPRESS_MS = 350
const IGNORE_SELECTOR =
  "input, select, textarea, [contenteditable]:not([contenteditable='false']), [data-swipe-ignore]"
const ZOOMED_SCALE = 1.01

export default class extends Controller {
  static targets = ["track", "slide", "status"]

  static values = {
    index: { type: Number, default: 0 },
    edgeGuard: { type: Number, default: 24 },
    threshold: { type: Number, default: 0.25 },
  }

  connect() {
    this.gesture = null
    this.animating = false
    this.suppressClicksUntil = 0

    this.onPointerDown = (event) => this.pointerDown(event)
    this.onClickCapture = (event) => this.suppressClick(event)
    this.onViewportResize = () => this.followZoom()
    this.element.addEventListener("pointerdown", this.onPointerDown)
    this.element.addEventListener("click", this.onClickCapture, true)
    window.visualViewport?.addEventListener("resize", this.onViewportResize)
    this.followZoom()

    // A Turbo snapshot or a half-finished animation can leave stray styles:
    // start from the server's slide, alone and at rest.
    this.resetTrack()
    this.showOnly(this.clamp(this.indexValue))
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this.onPointerDown)
    this.element.removeEventListener("click", this.onClickCapture, true)
    window.visualViewport?.removeEventListener("resize", this.onViewportResize)
    this.detachGestureListeners()
    this.gesture = null
    clearTimeout(this.finishTimer)
    if (this.frame) cancelAnimationFrame(this.frame)
  }

  get index() {
    return this.indexValue
  }

  get count() {
    return this.slideTargets.length
  }

  // ── API ────────────────────────────────────────────────────────────────

  next() {
    this.step(1)
  }

  previous() {
    this.step(-1)
  }

  // goTo(index) from code, or as a Stimulus action with
  // data-ui--swipe-deck-index-param. Returns true when the move started.
  goTo(target) {
    const to = typeof target === "number" ? target : Number(target?.params?.index)
    if (!Number.isInteger(to)) return false
    return this.requestChange(to, "api", 0)
  }

  step(delta) {
    const to = this.index + delta
    if (to < 0 || to >= this.count) {
      this.dispatch("overflow", { detail: { direction: delta > 0 ? "next" : "previous", cause: "api" } })
      return
    }
    this.requestChange(to, "api", 0)
  }

  // ── gesture ────────────────────────────────────────────────────────────

  pointerDown(event) {
    if (this.animating || this.gesture) return
    if (event.pointerType === "mouse" || event.isPrimary === false || event.button > 0) return
    if (event.target instanceof Element && event.target.closest(IGNORE_SELECTOR)) return
    if (this.zoomed()) return

    const viewport = window.innerWidth || document.documentElement.clientWidth
    if (event.clientX < this.edgeGuardValue || event.clientX > viewport - this.edgeGuardValue) return

    this.gesture = {
      pointerId: event.pointerId,
      target: event.target,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastTime: event.timeStamp,
      velocity: 0,
      dx: 0,
      axis: null,
      neighbour: null,
      rtl: getComputedStyle(this.element).direction === "rtl",
      width: 0,
    }
    this.attachGestureListeners()
  }

  pointerMove(event) {
    const gesture = this.gesture
    if (!gesture || event.pointerId !== gesture.pointerId) return

    const dx = event.clientX - gesture.startX
    const dy = event.clientY - gesture.startY

    if (!gesture.axis) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK_PX) return
      if (Math.abs(dy) >= Math.abs(dx)) {
        // A scroll, not a swipe: hand it back to the browser.
        this.endGesture()
        return
      }
      if (this.innerScrollerTakes(gesture.target, dx)) {
        this.endGesture()
        return
      }
      gesture.axis = "x"
      gesture.width = this.width()
      try {
        this.element.setPointerCapture?.(gesture.pointerId)
      } catch {
        // The pointer may already be gone; pointerup/cancel still end the gesture.
      }
    }

    const elapsed = event.timeStamp - gesture.lastTime
    if (elapsed > 0) gesture.velocity = (event.clientX - gesture.lastX) / elapsed
    gesture.lastX = event.clientX
    gesture.lastTime = event.timeStamp
    gesture.dx = dx

    const to = this.index + this.deltaFor(dx, gesture.rtl)
    const side = dx < 0 ? 1 : -1
    if (to >= 0 && to < this.count) {
      if (gesture.neighbour !== to) {
        if (gesture.neighbour !== null) this.concealSlide(gesture.neighbour)
        this.revealSlide(to, side)
        gesture.neighbour = to
      }
      this.renderOffset(dx)
    } else {
      if (gesture.neighbour !== null) this.concealSlide(gesture.neighbour)
      gesture.neighbour = null
      this.renderOffset(dx * RUBBER_BAND)
    }
  }

  pointerUp(event) {
    const gesture = this.gesture
    if (!gesture || event.pointerId !== gesture.pointerId) return
    this.endGesture()
    if (!gesture.axis) return

    // A swipe that ends over a button must not also press it.
    this.suppressClicksUntil = performance.now() + CLICK_SUPPRESS_MS

    const distance = Math.abs(gesture.dx)
    const flicked =
      distance >= MIN_FLICK_PX &&
      Math.abs(gesture.velocity) >= FLICK_VELOCITY_PX_PER_MS &&
      Math.sign(gesture.velocity) === Math.sign(gesture.dx)
    const passed = distance >= gesture.width * this.thresholdValue || flicked
    const delta = this.deltaFor(gesture.dx, gesture.rtl)
    const to = this.index + delta

    if (!passed) {
      this.snapBack(gesture.neighbour)
      return
    }
    if (to < 0 || to >= this.count) {
      this.snapBack(null)
      this.dispatch("overflow", { detail: { direction: delta > 0 ? "next" : "previous", cause: "swipe" } })
      return
    }
    if (!this.requestChange(to, "swipe", gesture.dx)) this.snapBack(gesture.neighbour)
  }

  pointerCancel(event) {
    const gesture = this.gesture
    if (!gesture || (event.pointerId !== undefined && event.pointerId !== gesture.pointerId)) return
    this.endGesture()
    if (gesture.axis) this.snapBack(gesture.neighbour)
  }

  suppressClick(event) {
    if (performance.now() >= this.suppressClicksUntil) return
    event.preventDefault()
    event.stopPropagation()
  }

  // ── moving between slides ──────────────────────────────────────────────

  requestChange(to, cause, fromOffset) {
    const from = this.index
    if (this.animating || to === from || to < 0 || to >= this.count) return false

    const before = this.dispatch("before-change", { detail: { from, to, cause }, cancelable: true })
    if (before.defaultPrevented) return false

    this.animateTo(to, cause, fromOffset)
    return true
  }

  animateTo(to, cause, fromOffset) {
    const forward = to > this.index
    const rtl = getComputedStyle(this.element).direction === "rtl"
    // The incoming slide sits on the side the content moves away from.
    const side = forward !== rtl ? 1 : -1
    const width = this.width()

    this.revealSlide(to, side)
    this.animating = true

    this.slide(fromOffset, -side * width, () => this.finish(to, cause))
  }

  snapBack(neighbour) {
    this.animating = true
    this.slide(this.currentOffset(), 0, () => {
      if (neighbour !== null && neighbour !== undefined) this.concealSlide(neighbour)
      this.resetTrack()
      this.animating = false
    })
  }

  // Moves the track from one offset to another, then calls `done`. With
  // reduced motion, or nothing to move, it finishes at once.
  slide(from, to, done) {
    const track = this.trackTarget
    const duration = this.reducedMotion() || from === to ? 0 : DURATION_MS

    // A frame queued by the last pointermove would overwrite the start position.
    if (this.frame) {
      cancelAnimationFrame(this.frame)
      this.frame = null
    }
    track.style.transition = "none"
    track.style.transform = `translateX(${from}px)`

    if (duration === 0) {
      done()
      return
    }

    // Commit the start position before transitioning away from it.
    void track.offsetWidth
    track.style.transition = `transform ${duration}ms cubic-bezier(0.2, 0.8, 0.2, 1)`
    track.style.transform = `translateX(${to}px)`

    let finished = false
    const finishOnce = () => {
      if (finished) return
      finished = true
      track.removeEventListener("transitionend", onEnd)
      clearTimeout(this.finishTimer)
      done()
    }
    const onEnd = (event) => {
      if (event.target === track) finishOnce()
    }
    track.addEventListener("transitionend", onEnd)
    // transitionend never fires if the element is hidden or the tab sleeps.
    this.finishTimer = setTimeout(finishOnce, duration + 50)
  }

  finish(to, cause) {
    const from = this.index
    const previous = this.slideTargets[from]
    const focusWasInside = Boolean(previous && previous.contains(document.activeElement))

    this.resetTrack()
    this.showOnly(to)
    this.indexValue = to
    this.animating = false

    const current = this.slideTargets[to]
    if (focusWasInside) current?.focus({ preventScroll: true })
    this.bringIntoView()
    this.announce(current?.dataset.label || current?.getAttribute("aria-label") || "")

    this.dispatch("change", { detail: { from, to, cause } })
  }

  // ── rendering helpers ──────────────────────────────────────────────────

  revealSlide(index, side) {
    const slide = this.slideTargets[index]
    if (!slide) return
    // Line the neighbour's top up with the top of the viewport, so a reader
    // halfway down a long slide sees the next one from its start.
    const top = Math.max(0, -this.element.getBoundingClientRect().top)
    slide.hidden = false
    slide.setAttribute("inert", "")
    Object.assign(slide.style, {
      position: "absolute",
      top: `${top}px`,
      left: `${side * 100}%`,
      width: "100%",
    })
  }

  concealSlide(index) {
    const slide = this.slideTargets[index]
    if (!slide || index === this.index) return
    slide.hidden = true
    slide.setAttribute("inert", "")
    this.clearSlideStyle(slide)
  }

  showOnly(index) {
    this.slideTargets.forEach((slide, i) => {
      this.clearSlideStyle(slide)
      slide.hidden = i !== index
      if (i === index) slide.removeAttribute("inert")
      else slide.setAttribute("inert", "")
    })
  }

  clearSlideStyle(slide) {
    slide.style.removeProperty("position")
    slide.style.removeProperty("top")
    slide.style.removeProperty("left")
    slide.style.removeProperty("width")
  }

  renderOffset(offset) {
    this.pendingOffset = offset
    if (this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.trackTarget.style.transition = "none"
      this.trackTarget.style.transform = `translateX(${this.pendingOffset}px)`
    })
  }

  currentOffset() {
    if (this.frame) {
      cancelAnimationFrame(this.frame)
      this.frame = null
    }
    if (typeof this.pendingOffset === "number") return this.pendingOffset
    const match = /translateX\((-?[\d.]+)px\)/.exec(this.trackTarget.style.transform)
    return match ? Number(match[1]) : 0
  }

  resetTrack() {
    if (!this.hasTrackTarget) return
    if (this.frame) {
      cancelAnimationFrame(this.frame)
      this.frame = null
    }
    this.pendingOffset = undefined
    this.trackTarget.style.removeProperty("transition")
    this.trackTarget.style.removeProperty("transform")
  }

  bringIntoView() {
    const top = this.element.getBoundingClientRect().top
    if (top < 0) window.scrollBy({ top, behavior: "auto" })
  }

  announce(text) {
    if (!this.hasStatusTarget || !text) return
    // Cleared first and set a frame later, so the same label is re-announced.
    this.statusTarget.textContent = ""
    requestAnimationFrame(() => {
      this.statusTarget.textContent = text
    })
  }

  // ── zoom and inner scrollers ───────────────────────────────────────────

  zoomed() {
    return (window.visualViewport?.scale ?? 1) > ZOOMED_SCALE
  }

  // touch-action is read when a touch starts, so lifting it between gestures
  // is enough: zoomed in, a sideways pan over the deck moves the viewport.
  followZoom() {
    this.element.style.touchAction = this.zoomed() ? "auto" : ""
  }

  // Whether something between the pressed element and the deck scrolls
  // sideways and can still scroll the way this drag goes (a finger moving
  // left scrolls toward the end). At its edge the drag is the deck's again,
  // as in a native pager.
  innerScrollerTakes(target, dx) {
    for (let node = target instanceof Element ? target : null; node && node !== this.element; node = node.parentElement) {
      const max = node.scrollWidth - node.clientWidth
      if (max <= 1) continue
      const style = getComputedStyle(node)
      if (style.overflowX !== "auto" && style.overflowX !== "scroll") continue
      // scrollLeft runs from 0 to -max in a right-to-left scroller.
      const scrolled = Math.abs(node.scrollLeft)
      const towardEnd = style.direction === "rtl" ? dx > 0 : dx < 0
      if (towardEnd ? scrolled < max - 1 : scrolled > 1) return true
    }
    return false
  }

  // ── small utilities ────────────────────────────────────────────────────

  deltaFor(dx, rtl) {
    const forward = rtl ? dx > 0 : dx < 0
    return forward ? 1 : -1
  }

  width() {
    return this.element.getBoundingClientRect().width || this.element.clientWidth || window.innerWidth
  }

  clamp(index) {
    if (this.count === 0) return 0
    return Math.min(Math.max(index, 0), this.count - 1)
  }

  reducedMotion() {
    return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches)
  }

  attachGestureListeners() {
    this.onPointerMove = (event) => this.pointerMove(event)
    this.onPointerUp = (event) => this.pointerUp(event)
    this.onPointerCancel = (event) => this.pointerCancel(event)
    // Only the deck losing ITS capture ends the gesture. A touch starts with
    // the pointer implicitly captured by the element pressed; capturing it on
    // the deck makes that element fire its own lostpointercapture, which
    // bubbles up here and is not a cancel.
    this.onLostCapture = (event) => {
      if (event.target === this.element) this.pointerCancel(event)
    }
    window.addEventListener("pointermove", this.onPointerMove)
    window.addEventListener("pointerup", this.onPointerUp)
    window.addEventListener("pointercancel", this.onPointerCancel)
    this.element.addEventListener("lostpointercapture", this.onLostCapture)
  }

  detachGestureListeners() {
    if (!this.onPointerMove) return
    window.removeEventListener("pointermove", this.onPointerMove)
    window.removeEventListener("pointerup", this.onPointerUp)
    window.removeEventListener("pointercancel", this.onPointerCancel)
    this.element.removeEventListener("lostpointercapture", this.onLostCapture)
    this.onPointerMove = null
    this.onPointerUp = null
    this.onPointerCancel = null
    this.onLostCapture = null
  }

  endGesture() {
    this.detachGestureListeners()
    try {
      if (this.gesture?.axis) this.element.releasePointerCapture?.(this.gesture.pointerId)
    } catch {
      // Already released.
    }
    this.gesture = null
  }
}
